import { WidgetType } from "@codemirror/view";
import {
    classifyMarkdownLinkTarget,
    faviconUrlFromExternalUrl,
    resolveMarkdownResourceUrl,
} from "../../../components/document-renderer/asset-refs";
import type { MarkdownLinkSyntaxKind } from "./scan";
import { attachSyntaxEditInteractions } from "../shared/widgets";

type LinkIconKind =
    | "anchor"
    | "web"
    | "asset"
    | "markdown"
    | "json"
    | "html"
    | "image"
    | "pdf"
    | "audio"
    | "video"
    | "csv"
    | "code"
    | "file";

interface PrettyMarkdownLinkOptions {
    syntaxKind?: MarkdownLinkSyntaxKind;
    title?: string | null;
}

export class PrettyMarkdownLinkWidget extends WidgetType {
    readonly syntaxKind: MarkdownLinkSyntaxKind;
    readonly markdownTitle: string | null;

    constructor(
        readonly label: string,
        readonly url: string,
        readonly documentPath: string | null,
        readonly editPosition: number,
        options: PrettyMarkdownLinkOptions = {},
    ) {
        super();
        this.syntaxKind = options.syntaxKind ?? "inline";
        this.markdownTitle = options.title ?? null;
    }

    eq(other: PrettyMarkdownLinkWidget): boolean {
        return other.label === this.label
            && other.url === this.url
            && other.documentPath === this.documentPath
            && other.editPosition === this.editPosition
            && other.syntaxKind === this.syntaxKind
            && other.markdownTitle === this.markdownTitle;
    }

    toDOM(): HTMLElement {
        const target = classifyMarkdownLinkTarget(this.url, this.documentPath);
        const isImageSyntax = this.syntaxKind === "image";
        const displayKind = isImageSyntax ? "image" : target.kind;
        const wrapper = document.createElement("span");
        wrapper.className = [
            "cm-magic-markdown-link",
            `cm-magic-markdown-link-kind-${displayKind}`,
            `cm-magic-markdown-link-source-${target.source}`,
            `cm-magic-markdown-link-syntax-${this.syntaxKind}`,
        ].join(" ");
        wrapper.title = [
            this.markdownTitle,
            target.localPath ?? target.externalUrl ?? this.url,
        ].filter(Boolean).join("\n");

        const imagePreviewUrl = isImageSyntax
            ? resolveMarkdownResourceUrl(this.url, this.documentPath)
            : null;
        const faviconUrl = !isImageSyntax && target.kind === "web"
            ? faviconUrlFromExternalUrl(target.externalUrl)
            : null;
        if (imagePreviewUrl) {
            const img = document.createElement("img");
            img.className = "cm-magic-markdown-link-thumbnail";
            img.alt = "";
            img.setAttribute("aria-hidden", "true");
            img.src = imagePreviewUrl;
            img.addEventListener("error", () => {
                img.replaceWith(createLinkIcon("image"));
            });
            wrapper.appendChild(img);
        } else if (faviconUrl) {
            const img = document.createElement("img");
            img.className = "cm-magic-markdown-link-favicon";
            img.alt = "";
            img.setAttribute("aria-hidden", "true");
            img.src = faviconUrl;
            img.addEventListener("error", () => {
                img.replaceWith(createLinkIcon(target.kind));
            });
            wrapper.appendChild(img);
        } else {
            wrapper.appendChild(createLinkIcon(displayKind));
        }

        const label = document.createElement("span");
        label.className = "cm-magic-markdown-link-label";
        label.textContent = this.label || labelFromUrl(this.url);
        wrapper.appendChild(label);

        attachSyntaxEditInteractions(wrapper, {
            keyboard: false,
            position: this.editPosition,
        });

        return wrapper;
    }

    ignoreEvent(): boolean {
        return true;
    }
}

export class PrettyMarkdownReferenceDefinitionWidget extends WidgetType {
    constructor(
        readonly id: string,
        readonly url: string,
        readonly title: string | null,
        readonly documentPath: string | null,
        readonly editPosition: number,
    ) {
        super();
    }

    eq(other: PrettyMarkdownReferenceDefinitionWidget): boolean {
        return other.id === this.id
            && other.url === this.url
            && other.title === this.title
            && other.documentPath === this.documentPath
            && other.editPosition === this.editPosition;
    }

    toDOM(): HTMLElement {
        const target = classifyMarkdownLinkTarget(this.url, this.documentPath);
        const wrapper = document.createElement("span");
        wrapper.className = [
            "cm-magic-markdown-refdef",
            `cm-magic-markdown-refdef-kind-${target.kind}`,
            `cm-magic-markdown-refdef-source-${target.source}`,
        ].join(" ");
        wrapper.title = [
            this.title,
            target.localPath ?? target.externalUrl ?? this.url,
        ].filter(Boolean).join("\n");

        const key = document.createElement("span");
        key.className = "cm-magic-markdown-refdef-key";
        key.textContent = `[${this.id}]`;
        wrapper.appendChild(key);

        const arrow = document.createElement("span");
        arrow.className = "cm-magic-markdown-refdef-arrow";
        arrow.textContent = "->";
        wrapper.appendChild(arrow);

        const isImageTarget = target.kind === "image";
        const imagePreviewUrl = isImageTarget
            ? resolveMarkdownResourceUrl(this.url, this.documentPath)
            : null;
        const faviconUrl = !isImageTarget && target.kind === "web"
            ? faviconUrlFromExternalUrl(target.externalUrl)
            : null;
        if (imagePreviewUrl) {
            const img = document.createElement("img");
            img.className = "cm-magic-markdown-link-thumbnail";
            img.alt = "";
            img.setAttribute("aria-hidden", "true");
            img.src = imagePreviewUrl;
            img.addEventListener("error", () => {
                img.replaceWith(createLinkIcon("image"));
            });
            wrapper.appendChild(img);
        } else if (faviconUrl) {
            const img = document.createElement("img");
            img.className = "cm-magic-markdown-link-favicon";
            img.alt = "";
            img.setAttribute("aria-hidden", "true");
            img.src = faviconUrl;
            img.addEventListener("error", () => {
                img.replaceWith(createLinkIcon(target.kind));
            });
            wrapper.appendChild(img);
        } else {
            wrapper.appendChild(createLinkIcon(target.kind));
        }

        const targetLabel = document.createElement("span");
        targetLabel.className = "cm-magic-markdown-refdef-target";
        targetLabel.textContent = this.title || labelFromUrl(this.url);
        wrapper.appendChild(targetLabel);

        attachSyntaxEditInteractions(wrapper, {
            keyboard: false,
            position: this.editPosition,
        });

        return wrapper;
    }

    ignoreEvent(): boolean {
        return true;
    }
}

function labelFromUrl(url: string): string {
    const cleaned = url.trim().replace(/[?#].*$/, "");
    const slash = cleaned.lastIndexOf("/");
    const fileName = slash >= 0 ? cleaned.slice(slash + 1) : cleaned;
    return fileName || url;
}

function createLinkIcon(kind: LinkIconKind): SVGElement {
    const ns = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(ns, "svg");
    svg.classList.add("cm-magic-markdown-link-icon");
    svg.setAttribute("aria-hidden", "true");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("fill", "none");
    svg.setAttribute("stroke", "currentColor");
    svg.setAttribute("stroke-width", "2");
    svg.setAttribute("stroke-linecap", "round");
    svg.setAttribute("stroke-linejoin", "round");

    for (const pathData of getIconPaths(kind)) {
        const path = document.createElementNS(ns, "path");
        path.setAttribute("d", pathData);
        svg.appendChild(path);
    }

    return svg;
}

function getIconPaths(kind: LinkIconKind): string[] {
    if (kind === "web" || kind === "html") {
        return [
            "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z",
            "M3.6 9h16.8",
            "M3.6 15h16.8",
            "M12 3a14 14 0 0 1 0 18",
            "M12 3a14 14 0 0 0 0 18",
        ];
    }
    if (kind === "anchor") {
        return [
            "M4 9h16",
            "M4 15h16",
            "M10 3 8 21",
            "M16 3l-2 18",
        ];
    }
    if (kind === "image" || kind === "video") {
        return [
            "M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2Z",
            "M14 2v6h6",
            "M8 17l2.5-3 2 2.5 2.5-3.5 3 4",
        ];
    }
    if (kind === "audio") {
        return [
            "M9 18V5l12-2v13",
            "M9 9l12-2",
            "M6 18a3 3 0 1 0 3 3v-3H6Z",
            "M18 16a3 3 0 1 0 3 3v-3h-3Z",
        ];
    }
    if (kind === "json" || kind === "code") {
        return [
            "M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2Z",
            "M14 2v6h6",
            "M10 13l-2 2 2 2",
            "M14 13l2 2-2 2",
        ];
    }
    if (kind === "csv") {
        return [
            "M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2Z",
            "M14 2v6h6",
            "M8 13h8",
            "M8 17h8",
        ];
    }
    return [
        "M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2Z",
        "M14 2v6h6",
    ];
}
